import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import MonthDropdown from "@/components/MonthDropdown";
import { trackOnboarding } from "@/lib/onboardingTrack";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar, Cell } from "recharts";

const CHART_COLORS = ["#2D5A40", "#86A893", "#E09E50", "#C45B4D", "#D0D9D3", "#437A55", "#A16A25", "#576A5E"];

// Recharts prop objects hoisted to module scope for referential stability.
const INITIAL_DIMENSION = { width: 1, height: 1 };
const BREAKDOWN_MARGIN = { top: 0, right: 10, left: 0, bottom: 0 };
const BAR_RADIUS = [0, 6, 6, 0];
const LINE_DOT = { r: 3 };

export default function Reports() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [cashflow, setCashflow] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [prevBreakdown, setPrevBreakdown] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [compare, setCompare] = useState(false);

  // "YYYY-MM" one month before the selected value.
  const prevMonth = useMemo(() => {
    const [y, m] = selectedMonth.split("-").map(Number);
    const d = new Date(y, m - 2, 1); // m-1 is current, so m-2 is previous
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, [selectedMonth]);

  // Initial load — cashflow (full window) + summary. Breakdown is refetched
  // whenever the picked month changes.
  useEffect(() => {
    trackOnboarding("first_view_reports");
    (async () => {
      const [cf, s] = await Promise.all([
        api.get("/reports/cashflow"),
        api.get("/dashboard/summary"),
      ]);
      setCashflow(cf.data);
      setSummary(s.data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/reports/expense-breakdown?month=${selectedMonth}`);
      setBreakdown(data);
    })();
  }, [selectedMonth]);

  // Previous-month breakdown is only fetched when the compare toggle is on —
  // avoids a needless request in the common (single-month) case.
  useEffect(() => {
    if (!compare) { setPrevBreakdown([]); return; }
    (async () => {
      const { data } = await api.get(`/reports/expense-breakdown?month=${prevMonth}`);
      setPrevBreakdown(data);
    })();
  }, [compare, prevMonth]);

  const cur = user?.currency || "USD";

  // Feed the month dropdown from whichever months the cashflow endpoint knows
  // about — that's the widest range on the page.
  const monthItems = useMemo(() => cashflow.map(r => ({ month: `${r.month}-01` })), [cashflow]);
  const selectedRow = useMemo(
    () => cashflow.find(r => r.month === selectedMonth) || null,
    [cashflow, selectedMonth]
  );
  const monthLabel = useMemo(
    () => new Date(`${selectedMonth}-01T00:00:00`).toLocaleString(undefined, { month: "long", year: "numeric" }),
    [selectedMonth]
  );
  const breakdownTotal = useMemo(
    () => breakdown.reduce((a, b) => a + Number(b.amount || 0), 0),
    [breakdown]
  );
  const prevBreakdownTotal = useMemo(
    () => prevBreakdown.reduce((a, b) => a + Number(b.amount || 0), 0),
    [prevBreakdown]
  );
  const prevMonthLabel = useMemo(
    () => new Date(`${prevMonth}-01T00:00:00`).toLocaleString(undefined, { month: "long", year: "numeric" }),
    [prevMonth]
  );

  // Union of categories from both months (order = current month first, then
  // any extra categories that only existed last month). Percent change per
  // category is used to color the delta badges.
  const comboBreakdown = useMemo(() => {
    if (!compare) return [];
    const prevMap = new Map(prevBreakdown.map(r => [r.category, r.amount]));
    const curMap = new Map(breakdown.map(r => [r.category, r.amount]));
    const cats = [...new Set([...breakdown.map(r => r.category), ...prevBreakdown.map(r => r.category)])];
    return cats.map(cat => {
      const cur = curMap.get(cat) || 0;
      const prev = prevMap.get(cat) || 0;
      const delta = cur - prev;
      const pct = prev > 0 ? (delta / prev) * 100 : (cur > 0 ? 100 : 0);
      return { category: cat, current: cur, previous: prev, delta, pct };
    }).sort((a, b) => (b.current + b.previous) - (a.current + a.previous));
  }, [compare, breakdown, prevBreakdown]);

  const totalDelta = breakdownTotal - prevBreakdownTotal;
  const totalPct = prevBreakdownTotal > 0
    ? (totalDelta / prevBreakdownTotal) * 100
    : (breakdownTotal > 0 ? 100 : 0);

  return (
    <div>
      <PageHeader subtitle={t("pages.reports.subtitle")} title={t("pages.reports.title")}/>

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm text-[var(--text-secondary)]">Month</label>
          <MonthDropdown
            items={monthItems}
            dateKey="month"
            value={selectedMonth}
            onChange={setSelectedMonth}
            testid="reports-month-picker"
          />
        </div>
        <label
          className="flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer select-none px-3 py-1.5 rounded-lg border border-[var(--border-default)] hover:bg-[var(--bg-secondary)]"
          data-testid="compare-toggle-label"
        >
          <input
            type="checkbox"
            checked={compare}
            onChange={e => setCompare(e.target.checked)}
            data-testid="compare-toggle"
            className="h-4 w-4 accent-[var(--accent-primary)]"
          />
          Compare with previous month
        </label>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mb-6">
        <div className="lg:col-span-2 card-flat p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow">{t("pages.reports.cashflow")}</div>
              <div className="text-base font-medium mt-1">{t("pages.reports.historic_forecast")}</div>
            </div>
          </div>
          <div style={{width:"100%", height:320}}>
            <ResponsiveContainer width="100%" height="100%" initialDimension={INITIAL_DIMENSION}>
              <LineChart data={cashflow}>
                <CartesianGrid stroke="#E2E4E0" strokeDasharray="3 3" vertical={false}/>
                <XAxis dataKey="month" tickLine={false} axisLine={false}/>
                <YAxis tickLine={false} axisLine={false} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}/>
                <Tooltip formatter={v => formatMoney(v, cur)}/>
                <Legend />
                <Line type="monotone" dataKey="income" name={t("pages.reports.income_h")} stroke="#2D5A40" strokeWidth={2.5} dot={LINE_DOT}/>
                <Line type="monotone" dataKey="expense" name={t("pages.reports.expense_h")} stroke="#C45B4D" strokeWidth={2.5} dot={LINE_DOT}/>
                <Line type="monotone" dataKey="net" name={t("pages.reports.net_h")} stroke="#E09E50" strokeWidth={2} strokeDasharray="6 4" dot={false}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-flat p-6">
          <div className="label-eyebrow">{compare ? `${prevMonthLabel} vs ${monthLabel}` : monthLabel}</div>
          <div className="text-base font-medium mt-1">{t("pages.reports.cat_breakdown")}</div>
          {(compare ? comboBreakdown.length === 0 : breakdown.length === 0) ? (
            <div className="text-sm text-[var(--text-muted)] mt-6" data-testid="breakdown-empty">
              No expenses recorded for {monthLabel}.
            </div>
          ) : compare ? (
            <>
              <div style={{width:"100%", height:Math.max(280, comboBreakdown.length * 44)}} className="mt-3">
                <ResponsiveContainer width="100%" height="100%" initialDimension={INITIAL_DIMENSION}>
                  <BarChart data={comboBreakdown} layout="vertical" margin={BREAKDOWN_MARGIN}>
                    <XAxis type="number" hide/>
                    <YAxis type="category" dataKey="category" width={90} tickLine={false} axisLine={false}/>
                    <Tooltip formatter={v => formatMoney(v, cur)}/>
                    <Legend wrapperStyle={{ fontSize: 12 }}/>
                    <Bar dataKey="previous" name={prevMonthLabel} fill="#86A893" radius={BAR_RADIUS} />
                    <Bar dataKey="current" name={monthLabel} fill="#2D5A40" radius={BAR_RADIUS} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 pt-3 border-t border-[var(--border-default)] space-y-2">
                <div className="flex items-center justify-between text-xs text-[var(--text-secondary)]">
                  <span>{prevMonthLabel}</span>
                  <span className="num-md">{formatMoney(prevBreakdownTotal, cur)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[var(--text-primary)]">{monthLabel}</span>
                  <span className="text-sm font-semibold num-md text-[var(--text-primary)]" data-testid="breakdown-month-total">
                    {formatMoney(breakdownTotal, cur)}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-1.5 border-t border-dashed border-[var(--border-default)]">
                  <span className="text-xs text-[var(--text-secondary)]">Change</span>
                  <span
                    className={`text-sm font-semibold num-md ${totalDelta > 0 ? "text-[var(--accent-danger)]" : totalDelta < 0 ? "text-[var(--accent-positive)]" : "text-[var(--text-secondary)]"}`}
                    data-testid="compare-delta"
                  >
                    {totalDelta > 0 ? "+" : ""}{formatMoney(totalDelta, cur)}
                    <span className="text-xs ml-1">({totalPct > 0 ? "+" : ""}{totalPct.toFixed(1)}%)</span>
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={{width:"100%", height:280}} className="mt-3">
                <ResponsiveContainer width="100%" height="100%" initialDimension={INITIAL_DIMENSION}>
                  <BarChart data={breakdown} layout="vertical" margin={BREAKDOWN_MARGIN}>
                    <XAxis type="number" hide/>
                    <YAxis type="category" dataKey="category" width={90} tickLine={false} axisLine={false}/>
                    <Tooltip formatter={v => formatMoney(v, cur)}/>
                    <Bar dataKey="amount" radius={BAR_RADIUS}>
                      {breakdown.map((b, i) => <Cell key={b.category || i} fill={CHART_COLORS[i%CHART_COLORS.length]}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 pt-3 border-t border-[var(--border-default)] flex items-center justify-between">
                <span className="text-sm font-semibold text-[var(--text-primary)]">Total for {monthLabel}</span>
                <span className="text-sm font-semibold num-md text-[var(--text-primary)]" data-testid="breakdown-month-total">
                  {formatMoney(breakdownTotal, cur)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {summary && (
        <div className="grid md:grid-cols-4 gap-5">
          <div className="card-surface p-5"><div className="label-eyebrow">{t("pages.reports.savings_rate")}</div><div className="num-lg mt-2">{summary.savings_rate}%</div></div>
          <div className="card-surface p-5"><div className="label-eyebrow">{t("pages.reports.investment_gain")}</div><div className={`num-lg mt-2 ${summary.investment_gain>=0?"text-[var(--accent-positive)]":"text-[var(--accent-danger)]"}`}>{formatMoney(summary.investment_gain, cur)}</div></div>
          <div className="card-surface p-5"><div className="label-eyebrow">{t("pages.reports.dti")}</div><div className="num-lg mt-2">{summary.monthly_income > 0 ? `${(summary.monthly_emi/summary.monthly_income*100).toFixed(1)}%` : "-"}</div></div>
          <div className="card-surface p-5"><div className="label-eyebrow">{t("pages.reports.health_score")}</div><div className="num-lg mt-2">{summary.health_score}/100</div></div>
        </div>
      )}

      <div className="mt-6 card-flat p-6">
        <div className="label-eyebrow">{t("pages.reports.cashflow_table")}</div>
        <table className="w-full text-sm mt-4">
          <thead className="text-[var(--text-secondary)]">
            <tr>
              <th className="text-left py-2">{t("pages.reports.month_h")}</th>
              <th className="text-right py-2">{t("pages.reports.income_h")}</th>
              <th className="text-right py-2">{t("pages.reports.expense_h")}</th>
              <th className="text-right py-2">{t("pages.reports.net_h")}</th>
              <th className="text-right py-2">{t("pages.reports.type_h")}</th>
            </tr>
          </thead>
          <tbody>
            {cashflow.map(row => {
              const isSel = row.month === selectedMonth;
              return (
                <tr
                  key={row.month}
                  className={`border-t border-[var(--border-default)] ${isSel ? "bg-[var(--bg-accent)]/40" : ""}`}
                  data-testid={isSel ? "cashflow-selected-row" : undefined}
                >
                  <td className={`py-2 ${isSel ? "font-semibold" : ""}`}>{row.month}</td>
                  <td className="text-right num-md">{formatMoney(row.income, cur)}</td>
                  <td className="text-right num-md">{formatMoney(row.expense, cur)}</td>
                  <td className={`text-right num-md ${row.net>=0?"text-[var(--accent-positive)]":"text-[var(--accent-danger)]"}`}>{formatMoney(row.net, cur)}</td>
                  <td className="text-right"><span className={`badge ${row.forecast ? "badge-warn" : "badge-neutral"}`}>{row.forecast ? t("pages.reports.forecast") : t("pages.reports.actual")}</span></td>
                </tr>
              );
            })}
          </tbody>
          {selectedRow && (
            <tfoot className="bg-[var(--bg-secondary)] border-t-2 border-[var(--border-default)]">
              <tr>
                <td className="py-3 text-sm font-semibold text-[var(--text-primary)]">
                  Total for {monthLabel}
                </td>
                <td className="text-right num-md text-sm font-semibold" data-testid="cashflow-month-income">
                  {formatMoney(selectedRow.income, cur)}
                </td>
                <td className="text-right num-md text-sm font-semibold" data-testid="cashflow-month-expense">
                  {formatMoney(selectedRow.expense, cur)}
                </td>
                <td className={`text-right num-md text-sm font-semibold ${selectedRow.net>=0?"text-[var(--accent-positive)]":"text-[var(--accent-danger)]"}`} data-testid="cashflow-month-net">
                  {formatMoney(selectedRow.net, cur)}
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
